import { BaseFieldProps } from "./BaseFieldProps";
import { URL_PATTERN } from "../../../../../constants/constants";
import { Typography } from "@optimizely/axiom";
import React, { useState } from "react";
import { handleFieldChange } from "./oidc-form-field-helpers";
import { InputFieldError } from "../../../InputFieldError/InputFieldError";
import classnames from "classnames";

interface OIDCUrlFieldProps extends BaseFieldProps {
    id: string;
    label: string;
    description: string;
    examples?: string[];
    placeholder?: string;
    hideHelperText?: boolean;
}

/**
 * Shared text input for the OIDC SSO setup URL fields. Validates the value against
 * URL_PATTERN on change (only when non-empty, or when required and empty), surfaces a
 * field-level error, and reports the error state back to the parent via the second
 * `onChange` argument so the form can disable Save.
 */
export const OIDCUrlField = ({
    id,
    label,
    description,
    examples,
    placeholder,
    hideHelperText,
    disabled,
    onChange,
    required = false,
    value = ""
}: OIDCUrlFieldProps) => {
    const [fieldError, setFieldError] = useState("");
    // TODO: Create InputField component that handles this and labels and notes
    const inputClasses = classnames("oui-text-input", {
        "oui-form-bad-news": !!fieldError
    });

    const describedBy = [`${id}-description`, fieldError && `${id}-error`].filter(Boolean).join(" ");

    const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value: newValue } = e.target;
        let hasError = false;
        if (required && !newValue) {
            setFieldError(`${label} is required.`);
            hasError = true;
        } else if (newValue && !newValue.match(URL_PATTERN)) {
            setFieldError(`${label} is not a valid url.`);
            hasError = true;
        } else {
            setFieldError("");
        }
        handleFieldChange({ e, onChange, hasError });
    };

    return (
        <>
            <label className="oui-label push-double--top" htmlFor={id}>
                {label}
                {required && !disabled && <span aria-label="(required)" className="oui-label--required" />}
            </label>
            <input
                aria-describedby={describedBy}
                className={inputClasses}
                disabled={disabled}
                id={id}
                placeholder={placeholder}
                type="text"
                value={value}
                onChange={onValueChange}
            />
            <span className="oui-form-note" id={`${id}-description`}>
                {description}
            </span>
            {!hideHelperText && !!examples?.length && (
                <Typography type="xs">
                    {examples.map((example) => (
                        <div key={example}>{example}</div>
                    ))}
                </Typography>
            )}
            {!!fieldError && <InputFieldError id={`${id}-error`}>{fieldError}</InputFieldError>}
        </>
    );
};

OIDCUrlField.displayName = "OIDCUrlField";
